'use client';

import type { Offer } from '@repo/contracts/marketplace/offers/types';
import { css } from '@repo/pandacss/css';
import { AnalyticsSkuViewed } from '@repo/platform-analytics/AnalyticsSkuViewed';
import { useElementInteracted } from '@repo/platform-analytics/hooks/useElementInteracted';
import { getOrCreateId } from '@repo/platform-utils/static-ids';
import { ChevronIcon } from '@repo/ui/icons/ChevronIcon';
import { Typography } from '@repo/ui/Typography';
import Image from 'next/image';
import { useRef } from 'react';
import { useBranchId, useFunnelStepId } from '@/features/funnel/model/Context/hooks';
import { usePageActionForBranch } from '@/features/funnel/model/usePageActionForBranch';
type Props = {
  offer: Offer;
  rank: number;
  onOpenDetails: (offer: Offer, skuImpressionId: string) => void;
};
export function Card({
  rank,
  offer,
  onOpenDetails
}: Props) {
  const activityIdRef = useRef('');
  const activityId = getOrCreateId(activityIdRef);
  const skuImpressionIdRef = useRef('');
  const skuImpressionId = getOrCreateId(skuImpressionIdRef);
  const stepId = useFunnelStepId();
  const branchId = useBranchId();
  const firePageAction = usePageActionForBranch({
    actionSection: 'early_recommendations_sidebar'
  });
  const fireElementInteracted = useElementInteracted({
    entityName: 'partner_modal_opened',
    sectionName: 'early_recommendations_sidebar',
    interactionType: 'click',
    itemPosition: rank - 1,
    eventId: activityId,
    additionalAttributes: {
      page_branch_name: branchId,
      page_name: stepId,
      sku_impression_id: skuImpressionId
    }
  });
  const handleClick = (offer_0: Offer) => {
    firePageAction({
      action_name: 'partner_modal_opened',
      action_input: offer_0.id,
      action_type: 'select',
      activity_id: activityId
    });
    fireElementInteracted();
    onOpenDetails(offer_0, skuImpressionId);
  };
  return <AnalyticsSkuViewed id={offer.id} name={offer.displayName} monetizable="no" overrideSkuImpressionId={skuImpressionId} impressionSection="early_recommendations_sidebar" impressionPosition={rank - 1} additionalAttributes={{
    page_branch_name: branchId,
    page_name: stepId
  }}>
      <button type="button" onClick={() => handleClick(offer)} aria-label={`View details for ${offer.displayName}`} className={css({
      appearance: 'none',
      textAlign: 'left',
      display: 'flex',
      width: '[100%]',
      alignSelf: 'center',
      alignItems: 'flex-start',
      gap: '3',
      bg: 'common.white',
      rounded: 'md',
      borderWidth: '1',
      borderColor: 'neutral.lighter',
      p: '[17px]',
      cursor: 'pointer',
      transition: '[transform 150ms ease]',
      _hover: {
        transform: '[scale(1.03)]'
      },
      _active: {
        transform: '[scale(1.01)]'
      }
    })}>
        <span className={css({
        flexShrink: '0',
        width: '[24px]',
        height: '[24px]',
        rounded: 'full',
        bg: 'greens.main',
        border: '[1.5px solid white]',
        alignSelf: 'flex-start',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'common.white',
        fontSize: '[14px]',
        fontWeight: 'bold',
        fontFamily: 'gotham'
      })}>
          {rank}
        </span>
        <Image src={offer.image.source} alt={offer.displayName} width={100} height={62} />
        <span className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '1',
        minWidth: '[0]',
        flex: '1'
      })}>
          <Typography as="span" variant="body" css={{
          fontWeight: 'bold',
          color: 'neutral.darkest',
          fontSize: '[14px]'
        }}>
            {offer.displayName}
          </Typography>
          <span className={css({
          width: '[fit-content]',
          display: 'flex',
          alignItems: 'center',
          gap: '1',
          fontWeight: 'medium',
          color: 'greens.main',
          fontSize: '[14px]'
        })}>
            Card details
            <ChevronIcon position="right" enableTransition width="16px" height="16px" />
          </span>
        </span>
      </button>
    </AnalyticsSkuViewed>;
}